import { useState, useEffect } from 'react'
import type { XRSessionState } from '@/types/xr'

interface ExtendedXRState extends XRSessionState {
  webXRSupported?: boolean
  arModeAvailable?: 'webxr' | 'device-orientation' | 'none'
  vrModeAvailable?: boolean
  isIOS?: boolean
  hasMotionPermission?: boolean
}

interface ARDebugInfoProps {
  xrState: ExtendedXRState
  show?: boolean
}

export function ARDebugInfo({ xrState, show = true }: ARDebugInfoProps) {
  const [supportInfo, setSupportInfo] = useState<any>({})
  const [logs, setLogs] = useState<string[]>([])
  
  // Check various support states
  useEffect(() => {
    const checkSupport = async () => {
      const info: any = {
        hasNavigatorXR: !!navigator.xr,
        vrSupported: false,
        arSupported: false,
        httpsConnection: location.protocol === 'https:',
        userAgent: navigator.userAgent,
        arModeAvailable: xrState.arModeAvailable,
        webXRSupported: xrState.webXRSupported,
        isIOS: xrState.isIOS,
        hasMotionPermission: xrState.hasMotionPermission,
        currentError: xrState.error,
        sessionType: xrState.sessionType,
        isActive: xrState.isActive
      }
      
      if (navigator.xr) {
        try {
          info.vrSupported = await navigator.xr.isSessionSupported('immersive-vr')
          info.arSupported = await navigator.xr.isSessionSupported('immersive-ar')
        } catch (e: any) {
          info.checkError = e.message
          addLog(`XR support check failed: ${e.message}`)
        }
      }
      
      // Check for ARCore/ARKit
      info.arCore = navigator.userAgent.includes('Android') && info.arSupported
      info.arKit = navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')
      
      // Check device capabilities
      info.hasOrientation = typeof DeviceOrientationEvent !== 'undefined'
      info.hasMotion = typeof DeviceMotionEvent !== 'undefined'
      info.hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
      
      setSupportInfo(info)
      addLog('Support check completed')
    }
    
    checkSupport()
  }, [xrState])
  
  // Add log entry
  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    setLogs(prev => [...prev.slice(-10), `[${timestamp}] ${message}`])
  }
  
  // Subscribe to XR state changes
  useEffect(() => {
    if (xrState.isActive) {
      addLog(`XR Session started: ${xrState.sessionType}`)
      if (xrState.sessionType === 'device-orientation-ar') {
        addLog('Using Device Orientation AR fallback')
        addLog('Camera background should be visible')
      }
    }
    if (xrState.error) {
      addLog(`Error: ${xrState.error}`)
    }
  }, [xrState.isActive, xrState.sessionType, xrState.error])
  
  if (!show) return null
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black/95 text-white p-4 text-xs font-mono max-h-96 overflow-y-auto z-50">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-bold text-sm">AR Debug Info</h4>
        <button 
          onClick={() => setLogs([])}
          className="text-gray-400 hover:text-white"
        >
          Clear
        </button>
      </div>
      
      {/* Support Status Grid */}
      <div className="grid grid-cols-2 gap-2 mb-4 border-b border-gray-700 pb-3">
        <div>Navigator.xr: {supportInfo.hasNavigatorXR ? '✅' : '❌'}</div>
        <div>HTTPS: {supportInfo.httpsConnection ? '✅' : '❌'}</div>
        <div>WebXR AR: {supportInfo.arSupported ? '✅' : '❌'}</div>
        <div>WebXR VR: {supportInfo.vrSupported ? '✅' : '❌'}</div>
        <div>Device Orientation: {supportInfo.hasOrientation ? '✅' : '❌'}</div>
        <div>Device Motion: {supportInfo.hasMotion ? '✅' : '❌'}</div>
        <div>Camera Access: {supportInfo.hasCamera ? '✅' : '❌'}</div>
        <div>iOS Device: {supportInfo.isIOS ? '✅' : '❌'}</div>
      </div>
      
      {/* Current State */}
      <div className="mb-4 border-b border-gray-700 pb-3">
        <h5 className="font-semibold mb-2">Current State</h5>
        <div className="grid grid-cols-2 gap-2">
          <div>AR Mode: <span className="text-green-400">{xrState.arModeAvailable || 'none'}</span></div>
          <div>Session: <span className="text-blue-400">{xrState.sessionType || 'none'}</span></div>
          <div>Active: {xrState.isActive ? '✅' : '❌'}</div>
          <div>Supported: {xrState.isSupported ? '✅' : '❌'}</div>
        </div>
        {xrState.error && (
          <div className="mt-2 text-red-400">
            Error: {xrState.error}
          </div>
        )}
      </div>
      
      {/* Logs */}
      <div>
        <h5 className="font-semibold mb-2">Event Log</h5>
        <div className="space-y-1 text-gray-300">
          {logs.length === 0 ? (
            <div className="text-gray-500">No events yet...</div>
          ) : (
            logs.map((log, i) => (
              <div key={i} className="text-xs">{log}</div>
            ))
          )}
        </div>
      </div>
      
      {/* User Agent */}
      <div className="mt-3 pt-3 border-t border-gray-700">
        <div className="text-xs text-gray-400 break-all">
          UA: {supportInfo.userAgent}
        </div>
      </div>
    </div>
  )
}